import {gql} from '@apollo/client';
import {groupByUniqueKey} from '@benchling/js-utils';

import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {MeasurementUnitFragment} from 'constants/MeasurementConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ContainerMeasurementsQuery} from 'samples/grids/hooks/__generated/ContainerMeasurementsQuery';

export const CONTAINER_MEASUREMENTS_QUERY = gql`
  # containers.contents does not query id
  # eslint-disable benchlint/id-field-required
  query ContainerMeasurementsQuery($containerIds: [StrictString]!) @skipAudit @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        quantity {
          value
          unit {
            ...MeasurementUnit
          }
        }
        contents {
          containedItem {
            ... on FolderItemInterface {
              id
            }
            ...InaccessibleInfoGraphQL
          }
          concentration {
            value
            unit {
              ...MeasurementUnit
            }
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${MeasurementUnitFragment.fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface ContainerMeasurementsData {
  id: string;
  quantity: {
    value: number | null;
    unit: MeasurementUnit | null;
  };
  /** Whether any of the container's contents is an item the current user cannot access. */
  hasInaccessibleContents: boolean;
  contents: Record<
    string,
    {
      concentration: {
        value: number | null;
        unit: MeasurementUnit | null;
      };
    }
  >;
}

export type ContainerMeasurementsByContainerId = Record<string, ContainerMeasurementsData>;

const transformQueryResult = (
  data: ContainerMeasurementsQuery['containers']
): ContainerMeasurementsByContainerId => {
  const containersWithContentsById = data
    .filter((node) => !isInaccessible(node))
    .map(
      (container: ExtractByTypename<ContainerMeasurementsQuery['containers'][number], 'ContainerNode'>) => ({
        ...container,
        hasInaccessibleContents: container.contents.some((content) => isInaccessible(content.containedItem)),
        contents: groupByUniqueKey(container.contents, (content) =>
          isInaccessible(content.containedItem) ? content.containedItem.invalidId : content.containedItem.id
        ),
      })
    );

  return groupByUniqueKey(containersWithContentsById, (container) => container.id);
};

export default function useContainerMeasurements(containerIds: Array<string>): {
  isPending: boolean;
  containersData?: ContainerMeasurementsByContainerId;
} {
  const queryResult = useSkippableBenchlingQuery<ContainerMeasurementsQuery>(CONTAINER_MEASUREMENTS_QUERY, {
    skip: containerIds.length === 0,
    variables: {containerIds},
    fetchPolicy: 'cache-and-network',
    renderCachedDataWhenRefetching: true,
  });

  if (queryResult.type !== QueryResultType.SUCCESS) {
    const isPending = queryResult.type === QueryResultType.PENDING;
    return {isPending};
  }

  return {
    isPending: false,
    containersData: transformQueryResult(queryResult.data.containers),
  };
}

export async function fetchContainerMeasurements(
  containerIds: Array<string>
): Promise<ContainerMeasurementsByContainerId> {
  const res = await doQuerySafe<ContainerMeasurementsQuery>(
    {query: CONTAINER_MEASUREMENTS_QUERY},
    {variables: {containerIds}}
  );

  return transformQueryResult(res.containers);
}
